import React, { useLayoutEffect, useState } from 'react';

import type { HelpCenterSiteTreeNode, SiteNavigationSplit } from './types';
import { splitSiteNavigation } from './split-site-navigation';

const isSameSplit = (a: SiteNavigationSplit, b: SiteNavigationSplit): boolean =>
	a.promotedFolders.length === b.promotedFolders.length &&
	a.overflowItems.length === b.overflowItems.length &&
	a.promotedFolders.every((folder, index) => folder.id === b.promotedFolders[index]?.id) &&
	a.overflowItems.every((item, index) => item.id === b.overflowItems[index]?.id);

export const useSiteNavigationOverflow = ({
	items,
	measurementRowRef,
	slotRef,
}: {
	items: readonly HelpCenterSiteTreeNode[];
	measurementRowRef: React.RefObject<HTMLDivElement>;
	slotRef: React.RefObject<HTMLDivElement>;
}): SiteNavigationSplit => {
	const [split, setSplit] = useState<SiteNavigationSplit>(() => ({
		promotedFolders: [],
		overflowItems: [...items],
	}));

	useLayoutEffect(() => {
		const slot = slotRef.current;
		const measurementRow = measurementRowRef.current;
		if (!slot || !measurementRow) {
			return;
		}

		let animationFrame: number | null = null;
		let hasUsableMeasurement = false;
		const measure = () => {
			const folderWidths = Object.fromEntries(
				Array.from(
					measurementRow.querySelectorAll<HTMLElement>('[data-site-navigation-folder-id]'),
					(element) => [
						element.getAttribute('data-site-navigation-folder-id') ?? '',
						element.getBoundingClientRect().width,
					],
				),
			);
			const moreItem = measurementRow.querySelector<HTMLElement>(
				'[data-site-navigation-more-item]',
			);
			const gapWidth = Number.parseFloat(window.getComputedStyle(measurementRow).columnGap) || 0;
			const availableWidth = slot.getBoundingClientRect().width;

			// The navbar row is `display: none` until its responsive breakpoint
			// resolves, so wait until the flex-growing slot has a usable width.
			if (availableWidth <= 0) {
				return;
			}
			hasUsableMeasurement = true;

			const next = splitSiteNavigation(items, {
				availableWidth,
				folderWidths,
				gapWidth,
				moreWidth: moreItem?.getBoundingClientRect().width ?? 0,
			});

			setSplit((previous) => (isSameSplit(previous, next) ? previous : next));
		};
		// Coalesce resize callbacks after the first usable synchronous measure.
		const scheduleMeasurement = () => {
			if (!hasUsableMeasurement) {
				measure();
				return;
			}
			if (animationFrame === null) {
				animationFrame = requestAnimationFrame(() => {
					animationFrame = null;
					measure();
				});
			}
		};

		// Measure before the browser paints so the first frame already shows the
		// correct split, rather than briefly rendering everything under More.
		measure();

		const observer = new ResizeObserver(scheduleMeasurement);
		observer.observe(slot);
		observer.observe(measurementRow);

		return () => {
			if (animationFrame !== null) {
				cancelAnimationFrame(animationFrame);
			}
			observer.disconnect();
		};
	}, [items, measurementRowRef, slotRef]);

	return split;
};
